import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { HttpErrorResponse } from "@angular/common/http";

import { ApplicationType, APPLICATION_TYPES, LogsService } from "./logs.service";
import { NotificationsService } from "../../../../shared/_services/notifications.service";

const MAX_RANGE_DAYS = 31;
const MS_PER_DAY = 86_400_000;

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-logs",
    templateUrl: "./logs.component.html",
    styleUrl: "./logs.component.scss",
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        MatDatepickerModule,
        MatNativeDateModule,
    ],
})
export class LogsComponent {
    private readonly fb = inject(FormBuilder);
    private readonly logsService = inject(LogsService);
    private readonly notifications = inject(NotificationsService);
    private readonly destroyRef = inject(DestroyRef);

    readonly applicationTypes = APPLICATION_TYPES;
    readonly maxRangeDays = MAX_RANGE_DAYS;
    readonly today = LogsComponent.atMidnight(new Date());

    readonly isDownloading = signal(false);

    readonly form: FormGroup = this.fb.group(
        {
            applicationType: [ApplicationType.Web, Validators.required],
            from: [this.today, Validators.required],
            to: [this.today, Validators.required],
        },
        { validators: [rangeValidator] },
    );

    get rangeError(): string | null {
        const errors = this.form.errors;
        return errors && typeof errors["range"] === "string" ? errors["range"] : null;
    }

    download(): void {
        if (this.isDownloading() || this.form.invalid) {
            return;
        }

        const { applicationType, from, to } = this.form.getRawValue() as {
            applicationType: ApplicationType;
            from: Date;
            to: Date;
        };
        const fromStr = LogsComponent.formatDate(from);
        const toStr = LogsComponent.formatDate(to);

        this.isDownloading.set(true);
        this.logsService
            .downloadAppLog(applicationType, fromStr, toStr)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (blob) => {
                    const filename =
                        fromStr === toStr
                            ? `${ApplicationType[applicationType]}_${fromStr}.zip`
                            : `${ApplicationType[applicationType]}_${fromStr}_to_${toStr}.zip`;
                    LogsComponent.downloadFile(blob, filename);
                    this.isDownloading.set(false);
                },
                error: (err: HttpErrorResponse) => {
                    this.isDownloading.set(false);
                    if (err.status === 404) {
                        this.notifications.showError("Logs", "No logs found for the selected range");
                    } else if (err.status === 400) {
                        this.notifications.showError("Logs", "Invalid request");
                    } else {
                        this.notifications.showError("Logs", "Failed to download logs");
                    }
                },
            });
    }

    private static atMidnight(date: Date): Date {
        const result = new Date(date);
        result.setHours(0, 0, 0, 0);
        return result;
    }

    private static formatDate(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }

    private static downloadFile(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }
}

function rangeValidator(control: AbstractControl): ValidationErrors | null {
    const from = control.get("from")?.value as Date | null;
    const to = control.get("to")?.value as Date | null;
    if (!from || !to) {
        return null;
    }
    // Compare by date components to avoid DST off-by-one across timezones.
    const fromUtc = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
    const toUtc = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
    if (toUtc < fromUtc) {
        return { range: "'To' date must be on or after 'From' date" };
    }
    const days = Math.round((toUtc - fromUtc) / MS_PER_DAY) + 1;
    if (days > MAX_RANGE_DAYS) {
        return { range: `Range exceeds ${MAX_RANGE_DAYS} days` };
    }
    return null;
}
